/**
 * Converts camelCase object keys to snake_case recursively
 * Used for converting client-side camelCase variables to server-side snake_case
 */
export function convertCamelCaseToSnakeCase<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertCamelCaseToSnakeCase) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const converted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = camelToSnake(key);
      converted[snakeKey] = convertCamelCaseToSnakeCase(value);
    }
    return converted as T;
  }

  return obj;
}

export function convertSnakeCaseToCamelCase<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(convertSnakeCaseToCamelCase) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const converted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = snakeToCamel(key);
      converted[camelKey] = convertSnakeCaseToCamelCase(value);
    }
    return converted as T;
  }

  return obj;
}

/**
 * Converts camelCase string to snake_case
 */
function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Converts snake_case string to camelCase
 */
function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// TypeScript utility types for case conversion
/**
 * Converts camelCase string literal type to snake_case
 */
type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? '_' : ''}${Lowercase<T>}${CamelToSnakeCase<U>}`
  : S;

/**
 * Converts snake_case string literal type to camelCase
 */
type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

/**
 * Recursively converts all keys in an object type from camelCase to snake_case
 */
export type CamelCaseToSnakeCaseKeys<T> = {
  [K in keyof T as K extends string ? CamelToSnakeCase<K> : K]: T[K] extends object
    ? T[K] extends Array<infer U>
      ? Array<U extends object ? CamelCaseToSnakeCaseKeys<U> : U>
      : CamelCaseToSnakeCaseKeys<T[K]>
    : T[K];
};

/**
 * Recursively converts all keys in an object type from snake_case to camelCase
 */
export type SnakeCaseToCamelCaseKeys<T> = {
  [K in keyof T as K extends string ? SnakeToCamelCase<K> : K]: T[K] extends object
    ? T[K] extends Array<infer U>
      ? Array<U extends object ? SnakeCaseToCamelCaseKeys<U> : U>
      : SnakeCaseToCamelCaseKeys<T[K]>
    : T[K];
};
